import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, Link as RouterLink, useParams } from 'react-router-dom';
import { connect } from 'react-redux';
import { 
  Box, Typography, Paper, Grid, TextField, FormControl, 
  FormLabel, FormControlLabel, Switch, InputLabel, Select,
  MenuItem, Divider, CircularProgress, Alert, Breadcrumbs,
  Chip, IconButton, Tooltip, Button, Link,
  FormHelperText, Accordion, AccordionSummary, AccordionDetails,
  Tabs, Tab, Slider, Card, CardContent, Avatar, Badge, Stepper, Step,
  StepLabel, StepContent, Autocomplete, ToggleButton, ToggleButtonGroup,
  InputAdornment
} from '@mui/material';
import { 
  CMButton, 
  CMButtonOutlined, 
  CMFormControlDark
} from '../../containers/styledComponents';
import { editSegmentRequest, clearState } from '../../redux/actions/segments/edit';
import { fetchRequest as fetchSegmentDetail } from '../../redux/actions/segments/detail';
import { fetchRequest as fetchAvailableFields, fetchTemplatesRequest } from '../../redux/actions/segments/fields';
import { useTheme } from '../../theme/ThemeContext';
import { SegmentsIcon } from '../global/CustomIcons';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoIcon from '@mui/icons-material/Info';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import ClearIcon from '@mui/icons-material/Clear';
import AddIcon from '@mui/icons-material/Add';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TimelineIcon from '@mui/icons-material/Timeline';
import TuneIcon from '@mui/icons-material/Tune';
import SettingsIcon from '@mui/icons-material/Settings';
import SpeedIcon from '@mui/icons-material/Speed';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import BoltIcon from '@mui/icons-material/Bolt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StorefrontIcon from '@mui/icons-material/Storefront';
import StorageIcon from '@mui/icons-material/Storage';
import CategoryIcon from '@mui/icons-material/Category';
import CountertopsIcon from '@mui/icons-material/Countertops';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import DataObjectIcon from '@mui/icons-material/DataObject';

// Help text for fields
const helpTexts = {
  name: "A descriptive name for your segment.",
  description: "A detailed description of what this segment represents.",
  purpose: "The intended use for this segment (model training, analysis, etc.).",
  features: "Data fields that will be used as inputs for prediction models.",
  labels: "Data fields that will be predicted by models trained on this segment.",
  interval: "Time interval in seconds between data points.",
  tolerance: "Acceptable time deviation in seconds.",
  tail_seconds: "Historical window in seconds to include in analysis.",
  strategy: "Data approximation strategy to use.",
  live: "Enable live processing of new data.",
  normalization_strategy: "Configure how missing data points are handled globally.",
  segment_strategies: "Configure field-specific normalization strategies."
};

// Add intelligent defaults for normalization
const defaultNormalizationStrategy = {
  primary_strategy: 'linear_interpolation', // Best general-purpose strategy for most datasets
  fallback_strategies: ['nearest_value', 'previous_value'], // Good fallbacks in case interpolation fails
  tolerance: 300 // 5 minutes is a reasonable default window
};

// Render the chip label for marketplace-aware fields.
// Format: "value (BTC volume)" — original signal name in normal
// weight, listing title in smaller parentheses on the same line.
// Native fields just show their key.
const renderChipLabel = (label, field) => {
  if (!field?.marketplace) return field?.display_name || label;
  const display = field.display_name
    || field.original_key
    || (field.key || '').split(':').slice(2).join(':')
    || field.key;
  const title = field.marketplace.listing_title;
  return (
    <Box component="span" sx={{
      display: 'inline-flex', alignItems: 'baseline', gap: 0.5,
    }}>
      <Box component="span">{display}</Box>
      {title && (
        <Box component="span" sx={{
          fontSize: 10.5,
          color: 'text.secondary',
        }}>
          ({title})
        </Box>
      )}
    </Box>
  );
};

// Component for displaying selected items with delete button
const SelectedItem = ({ label, onDelete, field }) => {
  const isMkt = !!field?.marketplace;
  return (
    <Chip
      label={renderChipLabel(label, field)}
      onDelete={onDelete}
      size="small"
      icon={isMkt
        ? <StorefrontIcon sx={{ fontSize: '14px !important' }} />
        : undefined}
      sx={{
        m: 0.5,
        backgroundColor: 'background.inputDark',
        '& .MuiChip-icon': { color: 'primary.main' },
        '& .MuiChip-deleteIcon': {
          color: 'text.secondary',
          '&:hover': { color: 'error.main' },
        },
      }}
    />
  );
};

// Unselected-pool chip: same friendly label, plus Storefront icon
// for marketplace fields.
const FieldChoiceChip = ({ field, disabled, onClick }) => {
  const isMkt = !!field?.marketplace;
  return (
    <Chip
      label={renderChipLabel(field.key, field)}
      size="small"
      icon={isMkt
        ? <StorefrontIcon sx={{ fontSize: '14px !important' }} />
        : undefined}
      onClick={onClick}
      disabled={disabled}
      sx={{ m: 0.5, '& .MuiChip-icon': { color: 'primary.main' } }}
    />
  );
};

// Field label with help icon
const FieldLabel = ({ label, helpText }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
    <Typography variant="body2" fontWeight={500}>{label}</Typography>
    <Tooltip title={helpText} arrow placement="top">
      <InfoIcon 
        fontSize="small" 
        sx={{ ml: 1, color: 'text.secondary', opacity: 0.7, fontSize: '0.9rem', cursor: 'help' }} 
      />
    </Tooltip>
  </Box>
);

// Add a new component for strategy selection
const StrategySelector = ({ value, onChange, availableStrategies, label, helperText, error }) => {
  if (!availableStrategies || availableStrategies.length === 0) {
    return (
      <TextField
        select
        label={label}
        value={value || ""}
        onChange={onChange}
        fullWidth
        helperText={helperText}
        error={error}
      >
        <MenuItem value="nearest_value">Nearest Value</MenuItem>
        <MenuItem value="previous_value">Previous Value</MenuItem>
        <MenuItem value="zero">Zero</MenuItem>
        <MenuItem value="mean">Mean</MenuItem>
        <MenuItem value="linear_interpolation">Linear Interpolation</MenuItem>
        <MenuItem value="none">None</MenuItem>
      </TextField>
    );
  }

  return (
    <TextField
      select
      label={label}
      value={value || ""}
      onChange={onChange}
      fullWidth
      helperText={helperText}
      error={error}
    >
      {availableStrategies.map(strategy => (
        <MenuItem key={strategy.id} value={strategy.id}>
          {strategy.name}
        </MenuItem>
      ))}
      {availableStrategies.length === 0 && <MenuItem value="none">None</MenuItem>}
    </TextField>
  );
};

// Add a component for displaying multiple selected strategies
const StrategyChipList = ({ strategies, onDelete }) => {
  if (!strategies || strategies.length === 0) return <Typography color="text.secondary">No fallback strategies selected</Typography>;
  
  return (
    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
      {strategies.map((strategy) => (
        <Chip
          key={strategy}
          label={strategy}
          onDelete={() => onDelete(strategy)}
          size="small"
          sx={{ m: 0.5 }}
        />
      ))}
    </Box>
  );
};

// Replace the current StrategyExplanation with a more interactive version
const StrategyExplanation = () => (
  <Card variant="outlined" sx={{ mb: 3, overflow: 'visible' }}>
    <CardContent sx={{ p: 0 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', p: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderTopLeftRadius: 'inherit', borderTopRightRadius: 'inherit' }}>
        <AutoAwesomeIcon sx={{ mr: 1 }} />
        <Typography variant="subtitle1" fontWeight="bold">Smart Data Normalization</Typography>
      </Box>
    </CardContent>
  </Card>
);

// Create a new component for the enhanced strategy configuration
const FeatureStrategyConfig = ({ 
  features, 
  normalizationStrategy, 
  segmentStrategies, 
  onStrategyChange, 
  onToleranceChange,
  onAddFallback,
  onRemoveFallback,
  onAddFieldStrategy,
  onRemoveFieldStrategy,
  formErrors,
  availableStrategies,
  templates,
  onApplyTemplate
}) => {
  const [selectedFeature, setSelectedFeature] = useState('');
  const [activeStep, setActiveStep] = useState(0);
  const [activeTab, setActiveTab] = useState(0);
  const [fieldStrategy, setFieldStrategy] = useState({
    primary_strategy: '',
    fallback_strategies: [],
    tolerance: 60
  });
  // Store custom configuration for potential restoration
  const [savedCustomConfig, setSavedCustomConfig] = useState(null);
  
  // Custom array comparison function to handle undefined and sort
  const compareArrays = (arr1, arr2) => {
    // Handle undefined/null arrays
    const array1 = arr1 || [];
    const array2 = arr2 || [];
    
    // Sort arrays to ensure consistent comparison
    const sorted1 = [...array1].sort();
    const sorted2 = [...array2].sort();
    
    // If lengths differ, they're not equal
    if (sorted1.length !== sorted2.length) return false;
    
    // Compare each element
    for (let i = 0; i < sorted1.length; i++) {
      if (sorted1[i] !== sorted2[i]) return false;
    }
    
    return true;
  };
  
  // Format tolerance value for display
  const formatToleranceValue = (value) => {
    if (!value && value !== 0) return 'Not set';
    if (value < 60) return `${value} seconds`;
    if (value < 3600) return `${Math.floor(value/60)} minutes`;
    return `${Math.floor(value/3600)} hours`;
  };
  
  // Get strategy name and description from backend data
  const getStrategyInfo = (strategyId, strategies) => {
    if (!strategyId) return { name: '', description: '' };
    
    // Find the strategy in the backend data
    const strategy = strategies?.find(s => s.id === strategyId);
    
    if (strategy) {
      return {
        name: strategy.name,
        description: strategy.description
      };
    }
    
    // Fallback for strategies not in the backend
    const fallbackStrategies = {
      'linear_interpolation': { name: 'Linear Interpolation', description: 'Estimates missing values using a straight line between points' },
      'nearest_value': { name: 'Nearest Value', description: 'Uses the nearest value within tolerance window' },
      'previous_value': { name: 'Previous Value', description: 'Uses the most recent previous value' },
      'db_previous': { name: 'Out of Range Previous', description: 'Queries the database for the previous value' },
      'db_nearest': { name: 'Out of Range Nearest', description: 'Queries the database for the nearest value' },
      'forward_value': { name: 'Forward Value', description: 'Uses the next available value' },
      'zero': { name: 'Zero', description: 'Replaces missing values with zero' },
      'mean': { name: 'Mean', description: 'Replaces missing values with the mean' },
      'median': { name: 'Median', description: 'Replaces missing values with the median' },
      'none': { name: 'None', description: 'Leaves missing values as None/NaN' }
    };
    
    return fallbackStrategies[strategyId] || { name: strategyId, description: '' };
  };
  
  // Handle feature selection
  const handleFeatureSelect = (event, newValue) => {
    setSelectedFeature(newValue);
    
    // If we already have a strategy for this field, load it
    if (newValue && segmentStrategies[newValue]) {
      setFieldStrategy(segmentStrategies[newValue]);
    } else {
      // Reset to default based on global strategy
      setFieldStrategy({
        primary_strategy: normalizationStrategy?.primary_strategy || defaultNormalizationStrategy.primary_strategy,
        fallback_strategies: [],
        tolerance: normalizationStrategy?.tolerance || defaultNormalizationStrategy.tolerance
      });
    }
  };
  
  // Handle primary strategy change for a field
  const handleFieldPrimaryStrategyChange = (event, newValue) => {
    setFieldStrategy(prev => ({
      ...prev,
      primary_strategy: newValue
    }));
  };
  
  // Handle tolerance change with slider
  const handleFieldToleranceChange = (event, newValue) => {
    setFieldStrategy(prev => ({
      ...prev,
      tolerance: newValue
    }));
  };
  
  // Add fallback strategy for field
  const handleAddFieldFallback = (strategy) => {
    if (!fieldStrategy.fallback_strategies.includes(strategy)) {
      setFieldStrategy(prev => ({
        ...prev,
        fallback_strategies: [...prev.fallback_strategies, strategy]
      }));
    }
  };
  
  // Remove fallback strategy for field
  const handleRemoveFieldFallback = (strategy) => {
    setFieldStrategy(prev => ({
      ...prev,
      fallback_strategies: prev.fallback_strategies.filter(s => s !== strategy)
    }));
  };
  
  // Save field strategy
  const handleSaveFieldStrategy = () => {
    onAddFieldStrategy(selectedFeature, fieldStrategy);
    setActiveStep(0);
    setSelectedFeature('');
  };
  
  // Handle tolerance change for global strategy
  const handleGlobalToleranceChange = (event, newValue) => {
    // Pass the raw value directly to the onToleranceChange handler
    onToleranceChange(newValue);
  };
  
  // Save custom configuration when detected
  useEffect(() => {
    // Check if we have a strategy configuration
    if (normalizationStrategy?.primary_strategy) {
      // Only run template matching if we have templates to compare against
      if (templates && templates.length > 0) {
        const matchingTemplate = templates.find(template => 
          normalizationStrategy.primary_strategy === template?.global_strategy?.primary_strategy &&
          compareArrays(normalizationStrategy.fallback_strategies, template?.global_strategy?.fallback_strategies) &&
          normalizationStrategy.tolerance === template?.global_strategy?.tolerance
        );
        
        // If we have a strategy but it doesn't match any template, save it as custom
        const isCustom = !matchingTemplate;
        
        if (isCustom) {
          const customConfig = {
            original_config: true,
            name: "Custom Configuration",
            global_strategy: {
              primary_strategy: normalizationStrategy.primary_strategy,
              fallback_strategies: normalizationStrategy.fallback_strategies || [],
              tolerance: normalizationStrategy.tolerance
            }
          };
          setSavedCustomConfig(customConfig);
        }
      } else {
        // No templates available, so consider current config as custom
        const customConfig = {
          original_config: true,
          name: "Custom Configuration",
          global_strategy: {
            primary_strategy: normalizationStrategy.primary_strategy,
            fallback_strategies: normalizationStrategy.fallback_strategies || [],
            tolerance: normalizationStrategy.tolerance
          }
        };
        setSavedCustomConfig(customConfig);
      }
    }
  }, [templates, normalizationStrategy]);
  
  // Render global strategy tab content
  const renderGlobalStrategyTab = () => {
    // Check if current configuration matches any template
    const matchingTemplate = templates?.find(template => 
      normalizationStrategy?.primary_strategy === template?.global_strategy?.primary_strategy &&
      compareArrays(normalizationStrategy?.fallback_strategies, template?.global_strategy?.fallback_strategies) &&
      normalizationStrategy?.tolerance === template?.global_strategy?.tolerance
    );
    
    // Only show Custom if we have a primary strategy selected but it doesn't match any template
    const isCustom = !matchingTemplate && normalizationStrategy?.primary_strategy;
    
    
    return (
      <Box>
        <Typography variant="subtitle1" gutterBottom>Global Normalization Strategy</Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          This strategy applies to all features unless overridden by a feature-specific strategy.
        </Typography>
        
        <Grid container spacing={3}>
          {/* Left side: Templates section */}
          <Grid item xs={12} md={4} sx={{ borderRight: '1px solid', borderColor: 'divider' }}>
            <Typography variant="subtitle2" gutterBottom>Available Templates</Typography>
            {templates && templates.length > 0 ? (
              <Box sx={{ maxHeight: '400px', overflowY: 'auto', pr: 2 }}>
                {/* Custom option when no template is exactly matching */}
                {isCustom && (
                  <Card 
                    variant="outlined"
                    onClick={() => {
                      // If custom config was saved, apply it
                      if (savedCustomConfig) {
                        onApplyTemplate(savedCustomConfig);
                      }
                    }}
                    sx={{ 
                      mb: 1,
                      cursor: 'pointer',
                      position: 'relative',
                      borderColor: 'primary.main',
                      borderWidth: 2,
                      bgcolor: 'primary.light',
                      '&:hover': {
                        boxShadow: 1
                      }
                    }}
                  >
                    <Box sx={{ 
                      p: 1.5, 
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between'
                    }}>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <TuneIcon color="primary" sx={{ mr: 1 }} />
                        <Box>
                          <Typography variant="subtitle2">
                            Custom Configuration
                          </Typography>
                          <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                            {getStrategyInfo(normalizationStrategy?.primary_strategy, availableStrategies).name}
                          </Typography>
                        </Box>
                      </Box>
                      <CheckCircleIcon color="primary" fontSize="small" />
                    </Box>
                  </Card>
                )}
                
                {templates.map((template, index) => {
                  const isSelected = 
                    normalizationStrategy?.primary_strategy === template.global_strategy.primary_strategy &&
                    compareArrays(normalizationStrategy?.fallback_strategies, template.global_strategy.fallback_strategies) &&
                    normalizationStrategy?.tolerance === template.global_strategy.tolerance;
                    
                  return (
                    <Card 
                      key={index}
                      variant="outlined" 
                      onClick={() => onApplyTemplate(template)}
                      sx={{ 
                        mb: 1,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        position: 'relative',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        borderWidth: isSelected ? 2 : 1,
                        bgcolor: isSelected ? 'primary.light' : 'background.paper',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1
                        }
                      }}
                    >
                      <Box sx={{ 
                        p: 1.5, 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between'
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {/* Choose icon based on template name */}
                          {template.name.toLowerCase().includes('sensor') && <TimelineIcon color="primary" sx={{ mr: 1 }} />}
                          {template.name.toLowerCase().includes('categorical') && <CategoryIcon color="secondary" sx={{ mr: 1 }} />}
                          {template.name.toLowerCase().includes('counter') && <CountertopsIcon sx={{ mr: 1, color: 'success.main' }} />}
                          {template.name.toLowerCase().includes('sparse') && <ScatterPlotIcon color="warning" sx={{ mr: 1 }} />}
                          {template.name.toLowerCase().includes('variable') && <ShowChartIcon sx={{ mr: 1, color: 'info.main' }} />}
                          {!template.name.toLowerCase().match(/(sensor|categorical|counter|sparse|variable)/) && 
                            <DataObjectIcon color="primary" sx={{ mr: 1 }} />}
                          <Box>
                            <Typography variant="subtitle2">
                              {template.name}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                              {getStrategyInfo(template.global_strategy.primary_strategy, availableStrategies).name}
                            </Typography>
                          </Box>
                        </Box>
                        
                        {isSelected && (
                          <CheckCircleIcon color="primary" fontSize="small" />
                        )}
                      </Box>
                    </Card>
                  );
                })}
              </Box>
            ) : (
              <Typography color="text.secondary">No templates available</Typography>
            )}
          </Grid>
          
          {/* Right side: Strategy configuration */}
          <Grid item xs={12} md={8}>
            <Card variant="outlined" sx={{ mb: 4 }}>
              <CardContent>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Primary Strategy
                  </Typography>
                  <Grid container spacing={2}>
                    {availableStrategies.map(strategy => (
                      <Grid item xs={6} sm={4} md={3} key={strategy.id}>
                        <Card 
                          variant="outlined" 
                          sx={{ 
                            height: '100%',
                            cursor: 'pointer',
                            bgcolor: normalizationStrategy?.primary_strategy === strategy.id ? 'primary.light' : 'background.paper',
                            borderColor: normalizationStrategy?.primary_strategy === strategy.id ? 'primary.main' : 'divider',
                            '&:hover': {
                              borderColor: 'primary.main',
                              boxShadow: 1
                            }
                          }}
                          onClick={() => onStrategyChange({ target: { value: strategy.id } })}
                        >
                          <Box sx={{ p: 2 }}>
                            <Typography variant="subtitle2" fontWeight="bold">{strategy.name}</Typography>
                            <Typography variant="caption" sx={{ display: 'block', mt: 1, minHeight: '2.5em' }}>
                              {strategy.description}
                            </Typography>
                            {normalizationStrategy?.primary_strategy === strategy.id && (
                              <CheckCircleIcon color="primary" sx={{ mt: 1, display: 'block' }} />
                            )}
                          </Box>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
                
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Tolerance: {formatToleranceValue(normalizationStrategy?.tolerance || 0)}
                  </Typography>
                  <Slider
                    value={typeof normalizationStrategy?.tolerance === 'number' ? normalizationStrategy.tolerance : 0}
                    min={10}
                    max={3600}
                    step={10}
                    onChange={handleGlobalToleranceChange}
                    valueLabelDisplay="auto"
                    valueLabelFormat={formatToleranceValue}
                    marks={[
                      { value: 60, label: '1m' },
                      { value: 300, label: '5m' },
                      { value: 1800, label: '30m' },
                      { value: 3600, label: '1h' },
                    ]}
                    sx={{ mt: 1, mb: 2 }}
                  />
                </Box>
                
                <Box>
                  <Typography variant="subtitle2" gutterBottom>Fallback Strategies</Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Applied in order if the primary strategy fails to find a value
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                    {(normalizationStrategy?.fallback_strategies || []).map((strategy, index) => (
                      <Chip
                        key={strategy}
                        label={`${index + 1}. ${getStrategyInfo(strategy, availableStrategies).name}`}
                        onDelete={() => onRemoveFallback(strategy)}
                        color="primary"
                        variant="outlined"
                      />
                    ))}
                    {(!normalizationStrategy?.fallback_strategies || normalizationStrategy.fallback_strategies.length === 0) && (
                      <Typography variant="body2" color="text.secondary">
                        No fallbacks configured
                      </Typography>
                    )}
                  </Box>
                  
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>Add Fallback</InputLabel>
                    <Select
                      value=""
                      onChange={(e) => onAddFallback(e.target.value)}
                      label="Add Fallback"
                      disabled={!normalizationStrategy?.primary_strategy}
                    >
                      {availableStrategies
                        .filter(s => s.id !== normalizationStrategy?.primary_strategy && 
                          !(normalizationStrategy?.fallback_strategies || []).includes(s.id))
                        .map(strategy => (
                          <MenuItem key={strategy.id} value={strategy.id}>
                            {strategy.name}
                          </MenuItem>
                        ))}
                    </Select>
                  </FormControl>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Box>
    );
  };
  
  // Render feature strategy tab content
  const renderFeatureStrategyTab = () => (
    <Box>
      <Typography variant="subtitle1" gutterBottom>
        Fine Grained Strategy
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        Customize how missing data is handled for specific features
      </Typography>
      
      <Stepper activeStep={activeStep} orientation="vertical">
        <Step>
          <StepLabel>
            <Typography variant="subtitle2">Select a feature to configure</Typography>
          </StepLabel>
          <StepContent>
            <Box sx={{ my: 2 }}>
              <Autocomplete
                value={selectedFeature}
                onChange={handleFeatureSelect}
                options={features.filter(f => !segmentStrategies[f])}
                renderInput={(params) => (
                  <TextField {...params} label="Select Feature" variant="outlined" />
                )}
                fullWidth
                sx={{ mb: 2 }}
              />
              
              {Object.keys(segmentStrategies || {}).length > 0 && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="subtitle2" gutterBottom>Configured Features:</Typography>
                  <Grid container spacing={2}>
                    {Object.entries(segmentStrategies || {}).map(([field, strategy]) => (
                      <Grid item xs={12} sm={6} key={field}>
                        <Card variant="outlined" sx={{ position: 'relative' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => onRemoveFieldStrategy(field)}
                            sx={{ position: 'absolute', top: 8, right: 8 }}
                          >
                            <ClearIcon fontSize="small" />
                          </IconButton>
                          <CardContent>
                            <Typography variant="subtitle2" noWrap>{field}</Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <TuneIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                              <Typography variant="body2">
                                {getStrategyInfo(strategy.primary_strategy, availableStrategies).name}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                              <SpeedIcon fontSize="small" sx={{ mr: 1, color: 'secondary.main' }} />
                              <Typography variant="body2">
                                {formatToleranceValue(strategy.tolerance)}
                              </Typography>
                            </Box>
                            {strategy.fallback_strategies?.length > 0 && (
                              <Box sx={{ mt: 1 }}>
                                <Typography variant="caption" color="text.secondary">
                                  Fallbacks: {strategy.fallback_strategies.map(s => getStrategyInfo(s, availableStrategies).name).join(', ')}
                                </Typography>
                              </Box>
                            )}
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Box>
            
            <Box sx={{ mb: 2, mt: 1 }}>
              <Button
                variant="contained"
                onClick={() => setActiveStep(1)}
                disabled={!selectedFeature}
                sx={{ mr: 1 }}
              >
                Continue
              </Button>
            </Box>
          </StepContent>
        </Step>
        
        <Step>
          <StepLabel>
            <Typography variant="subtitle2">Configure strategy for {selectedFeature}</Typography>
          </StepLabel>
          <StepContent>
            <Box sx={{ py: 2 }}>
              <Typography variant="subtitle2" gutterBottom>Primary Strategy</Typography>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                {availableStrategies.slice(0, 6).map(strategy => (
                  <Grid item xs={6} sm={4} key={strategy.id}>
                    <Card 
                      variant="outlined" 
                      sx={{ 
                        height: '100%',
                        cursor: 'pointer',
                        bgcolor: fieldStrategy.primary_strategy === strategy.id ? 'primary.light' : 'background.paper',
                        borderColor: fieldStrategy.primary_strategy === strategy.id ? 'primary.main' : 'divider',
                        '&:hover': {
                          borderColor: 'primary.main',
                          boxShadow: 1
                        }
                      }}
                      onClick={() => handleFieldPrimaryStrategyChange(null, strategy.id)}
                    >
                      <Box sx={{ p: 2 }}>
                        <Typography variant="subtitle2">{strategy.name}</Typography>
                        {fieldStrategy.primary_strategy === strategy.id && (
                          <CheckCircleIcon color="primary" sx={{ mt: 1, display: 'block' }} />
                        )}
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
              
              <FormControl fullWidth variant="outlined" sx={{ mb: 3 }}>
                <InputLabel>More Strategies</InputLabel>
                <Select
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      handleFieldPrimaryStrategyChange(null, e.target.value);
                    }
                  }}
                  label="More Strategies"
                >
                  {availableStrategies.slice(6).map(strategy => (
                    <MenuItem key={strategy.id} value={strategy.id}>
                      {strategy.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              <Typography variant="subtitle2" gutterBottom>
                Tolerance: {formatToleranceValue(fieldStrategy.tolerance)}
              </Typography>
              <Slider
                value={fieldStrategy.tolerance}
                min={10}
                max={3600}
                step={10}
                onChange={handleFieldToleranceChange}
                valueLabelDisplay="auto"
                valueLabelFormat={formatToleranceValue}
                marks={[
                  { value: 60, label: '1m' },
                  { value: 300, label: '5m' },
                  { value: 1800, label: '30m' },
                  { value: 3600, label: '1h' },
                ]}
                sx={{ mt: 4, mb: 3 }}
              />
              
              <Typography variant="subtitle2" gutterBottom>Fallback Strategies</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
                {fieldStrategy.fallback_strategies.map((strategy, index) => (
                  <Chip
                    key={strategy}
                    label={`${index + 1}. ${getStrategyInfo(strategy, availableStrategies).name}`}
                    onDelete={() => handleRemoveFieldFallback(strategy)}
                    color="primary"
                    variant="outlined"
                  />
                ))}
                {fieldStrategy.fallback_strategies.length === 0 && (
                  <Typography variant="body2" color="text.secondary">
                    No fallbacks configured
                  </Typography>
                )}
              </Box>
              
              <FormControl fullWidth variant="outlined" sx={{ mt: 2 }}>
                <InputLabel>Add Fallback</InputLabel>
                <Select
                  value=""
                  onChange={(e) => handleAddFieldFallback(e.target.value)}
                  label="Add Fallback"
                >
                  {availableStrategies
                    .filter(s => s.id !== fieldStrategy.primary_strategy && !fieldStrategy.fallback_strategies.includes(s.id))
                    .map(strategy => (
                      <MenuItem key={strategy.id} value={strategy.id}>
                        {strategy.name}
                      </MenuItem>
                    ))}
                </Select>
              </FormControl>
            </Box>
            
            <Box sx={{ mb: 2, display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                onClick={() => setActiveStep(0)}
              >
                Back
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveFieldStrategy}
                endIcon={<CheckCircleIcon />}
              >
                Save Strategy
              </Button>
            </Box>
          </StepContent>
        </Step>
      </Stepper>
    </Box>
  );

  return (
    <Box>
      <Tabs
        value={activeTab}
        onChange={(e, newValue) => setActiveTab(newValue)}
        aria-label="strategy tabs"
        sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}
      >
        <Tab label="Global Strategy" value={0} />
        <Tab label="Fine Grained Strategy" value={1} />
      </Tabs>
      
      {activeTab === 0 ? renderGlobalStrategyTab() : renderFeatureStrategyTab()}
    </Box>
  );
};

const EditSegmentPage = ({ 
  fetchSegmentDetail,
  editSegment,
  fetchAvailableFields,
  fetchTemplates,
  segmentData,
  segmentLoading,
  segmentError,
  fieldsData,
  fieldsLoading,
  fieldsError,
  templatesData,
  templatesLoading,
  isLoading, 
  response, 
  error,
  clearState
}) => {
  const { segmentId } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [workspaceId, setWorkspaceId] = useState('');
  
  // Form values
  const [formValues, setFormValues] = useState({
    name: '',
    description: '',
    purpose: '',
    features: [],
    labels: [],
    interval: 300,
    tolerance: 60,
    tail_seconds: 31536000, // Default 1 year in seconds
    strategy: 'approximated',
    live: false,
    normalization_strategy: {
      primary_strategy: 'nearest_value',
      fallback_strategies: [],
      tolerance: 60
    },
    segment_strategies: {}
  });

  const [formErrors, setFormErrors] = useState({});
  const [availableFields, setAvailableFields] = useState([]);
  const [availableStrategies, setAvailableStrategies] = useState([]);
  const [selectedField, setSelectedField] = useState('');
  const [fieldStrategy, setFieldStrategy] = useState({
    primary_strategy: '',
    fallback_strategies: [],
    tolerance: 60
  });
  const [loaded, setLoaded] = useState(false);
  
  // Get workspace ID from localStorage
  useEffect(() => {
    const id = localStorage.getItem('WORKSPACE_ID');
    if (id) {
      setWorkspaceId(id);
    }
  }, []);

  // Fetch segment detail when component mounts
  useEffect(() => {
    if (segmentId) {
      fetchSegmentDetail(segmentId);
    }
  }, [fetchSegmentDetail, segmentId]);

  // Fetch available fields and templates
  useEffect(() => {
    if (workspaceId) {
      fetchAvailableFields();
      fetchTemplates(); // Ensure templates are loaded
    }
  }, [fetchAvailableFields, fetchTemplates, workspaceId]);

  // Process fields data when it's loaded
  useEffect(() => {
    if (fieldsData?.workspace_analysis) {
      setAvailableFields(fieldsData.workspace_analysis);
    }
    if (fieldsData?.normalization_strategies?.primary_strategies) {
      setAvailableStrategies(fieldsData.normalization_strategies.primary_strategies);
    }
  }, [fieldsData]);

  // Populate form with segment data when loaded
  useEffect(() => {
    if (segmentData && !loaded) {
      // Log the raw segment data to help diagnose issues
      
      // Extract features and labels from the nested structure
      const features = segmentData.fields?.features?.map(f => f.name) || [];
      const labels = segmentData.fields?.labels?.map(l => l.name) || [];
      
      // Extract normalization strategy from the correct path
      const normalizationStrategy = segmentData.normalization?.global_strategy;
      const segmentStrategies = segmentData.normalization?.field_strategies || {};
      
      
      // Create the form values with the correct data structure
      const newFormValues = {
        id: segmentData.id,
        name: segmentData.name,
        description: segmentData.description,
        purpose: segmentData.purpose,
        features: features,
        labels: labels,
        interval: segmentData.configuration?.interval || 300,
        tolerance: segmentData.configuration?.tolerance || 60,
        tail_seconds: segmentData.configuration?.tail_seconds || 31536000,
        strategy: segmentData.configuration?.strategy || 'approximated',
        live: segmentData.configuration?.live || false,
        normalization_strategy: normalizationStrategy || defaultNormalizationStrategy,
        segment_strategies: segmentStrategies
      };
      
      
      setFormValues(newFormValues);
      setLoaded(true);
      
      // Log a confirmation that data is loaded
    }
  }, [segmentData, loaded]);

  // Clear redux state on unmount
  useEffect(() => {
    return () => {
      clearState();
    };
  }, [clearState]);

  // Redirect after successful update
  useEffect(() => {
    if (response?.message === "Segment updated successfully") {
      navigate('/segments');
    }
  }, [response, navigate]);

  // Add the debug useEffect
  useEffect(() => {
    if (templatesData && templatesData.templates) {
    }
  }, [templatesData]);

  // Debug useEffect to track normalization_strategy changes
  useEffect(() => {
  }, [formValues.normalization_strategy]);

  // Add a debug useEffect to verify the tolerance value in normalization_strategy after component is mounted
  useEffect(() => {
    if (loaded && formValues.normalization_strategy) {
    }
  }, [loaded]);

  // Add the debug useEffect for templates matching
  useEffect(() => {
    if (loaded && formValues.normalization_strategy && templatesData?.templates) {
      // Helper function for array comparison in this debug context
      const compareArraysLocal = (arr1, arr2) => {
        const array1 = arr1 || [];
        const array2 = arr2 || [];
        const sorted1 = [...array1].sort();
        const sorted2 = [...array2].sort();
        if (sorted1.length !== sorted2.length) return false;
        for (let i = 0; i < sorted1.length; i++) {
          if (sorted1[i] !== sorted2[i]) return false;
        }
        return true;
      };

      // Log the current normalization strategy and templates for debugging
    }
  }, [loaded, formValues.normalization_strategy, templatesData]);

  // Input change handler
  const handleInputChange = (e) => {
    const { name, value, checked, type } = e.target;
    
    // Special handling for checkboxes
    if (type === 'checkbox') {
      setFormValues(prev => ({
        ...prev,
        [name]: checked
      }));
      return;
    }
    
    // Special handling for numeric fields
    if (name === 'interval' || name === 'tolerance' || name === 'tail_seconds') {
      setFormValues(prev => ({
        ...prev,
        [name]: parseInt(value, 10) || 0
      }));
      return;
    }
    
    // Default handling for text fields
    setFormValues(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Handle adding a feature
  const handleAddFeature = (featureKey) => {
    if (!formValues.features.includes(featureKey)) {
      setFormValues(prev => ({
        ...prev,
        features: [...prev.features, featureKey]
      }));
    }
  };

  // Handle removing a feature
  const handleRemoveFeature = (featureKey) => {
    setFormValues(prev => ({
      ...prev,
      features: prev.features.filter(f => f !== featureKey)
    }));
  };

  // Handle adding a label
  const handleAddLabel = (labelKey) => {
    if (!formValues.labels.includes(labelKey)) {
      setFormValues(prev => ({
        ...prev,
        labels: [...prev.labels, labelKey]
      }));
    }
  };

  // Handle removing a label
  const handleRemoveLabel = (labelKey) => {
    setFormValues(prev => ({
      ...prev,
      labels: prev.labels.filter(l => l !== labelKey)
    }));
  };

  // Validate form before submission
  const validateForm = () => {
    const errors = {};
    
    if (!formValues.name.trim()) {
      errors.name = 'Name is required';
    }
    
    if (formValues.features.length === 0) {
      errors.features = 'At least one feature is required';
    }
    
    if (formValues.labels.length === 0) {
      errors.labels = 'At least one label is required';
    }
    
    if (formValues.interval <= 0) {
      errors.interval = 'Interval must be greater than 0';
    }
    
    // Use defaultNormalizationStrategy if not provided
    const normalizationStrategy = formValues.normalization_strategy || defaultNormalizationStrategy;
    
    if (!normalizationStrategy.primary_strategy) {
      errors.normalization_strategy = 'Primary normalization strategy is required';
    }
    
    if (normalizationStrategy.tolerance <= 0) {
      errors.strategy_tolerance = 'Normalization strategy tolerance must be greater than 0';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Form submission handler
  const handleSubmit = (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    const payload = {
      name: formValues.name,
      fields: {
        features: formValues.features.map(name => ({ name, role: 'feature' })),
        labels: formValues.labels.map(name => ({ name, role: 'label' }))
      },
      configuration: {
        interval: formValues.interval,
        tail_seconds: formValues.tail_seconds,
        strategy: formValues.strategy,
        live: formValues.live
      },
      normalization: {
        global_strategy: formValues.normalization_strategy,
        field_strategies: formValues.segment_strategies
      },
      workspace_id: workspaceId
    };
    
    editSegment(segmentId, payload);
  };

  const handleCancel = () => {
    navigate('/segments');
  };

  // Add a handler for primary strategy changes
  const handlePrimaryStrategyChange = (e) => {
    const { value } = e.target;
    setFormValues(prev => ({
      ...prev,
      normalization_strategy: {
        ...prev.normalization_strategy,
        primary_strategy: value
      }
    }));
  };

  // Add a handler for fallback strategy changes
  const handleAddFallbackStrategy = (strategy) => {
    if (!formValues.normalization_strategy.fallback_strategies.includes(strategy)) {
      setFormValues(prev => ({
        ...prev,
        normalization_strategy: {
          ...prev.normalization_strategy,
          fallback_strategies: [...prev.normalization_strategy.fallback_strategies, strategy]
        }
      }));
    }
  };

  const handleRemoveFallbackStrategy = (strategy) => {
    setFormValues(prev => ({
      ...prev,
      normalization_strategy: {
        ...prev.normalization_strategy,
        fallback_strategies: prev.normalization_strategy.fallback_strategies.filter(s => s !== strategy)
      }
    }));
  };

  // Add handlers for field-specific strategies
  const handleFieldSelection = (e) => {
    const field = e.target.value;
    setSelectedField(field);
    
    // If we already have a strategy for this field, load it
    if (formValues.segment_strategies[field]) {
      setFieldStrategy(formValues.segment_strategies[field]);
    } else {
      // Reset to default
      setFieldStrategy({
        primary_strategy: '',
        fallback_strategies: [],
        tolerance: 60
      });
    }
  };

  const handleFieldPrimaryStrategyChange = (e) => {
    const { value } = e.target;
    setFieldStrategy(prev => ({
      ...prev,
      primary_strategy: value
    }));
  };

  const handleAddFieldFallbackStrategy = (strategy) => {
    if (!fieldStrategy.fallback_strategies.includes(strategy)) {
      setFieldStrategy(prev => ({
        ...prev,
        fallback_strategies: [...prev.fallback_strategies, strategy]
      }));
    }
  };

  const handleRemoveFieldFallbackStrategy = (strategy) => {
    setFieldStrategy(prev => ({
      ...prev,
      fallback_strategies: prev.fallback_strategies.filter(s => s !== strategy)
    }));
  };

  const handleFieldToleranceChange = (e) => {
    const { value } = e.target;
    setFieldStrategy(prev => ({
      ...prev,
      tolerance: parseInt(value, 10) || 0
    }));
  };

  const handleAddFieldStrategy = (field, strategy) => {
    if (field && strategy.primary_strategy) {
      setFormValues(prev => ({
        ...prev,
        segment_strategies: {
          ...prev.segment_strategies,
          [field]: strategy
        }
      }));
    }
  };

  const handleRemoveFieldStrategy = (field) => {
    setFormValues(prev => {
      const newStrategies = { ...prev.segment_strategies };
      delete newStrategies[field];
      return {
        ...prev,
        segment_strategies: newStrategies
      };
    });
  };

  // Add handler for strategy tolerance changes
  const handleStrategyToleranceChange = (e) => {
    // Handle both slider events (which pass newValue directly) and regular inputs (which use e.target.value)
    const value = e.target ? e.target.value : e;
    
    setFormValues(prev => ({
      ...prev,
      normalization_strategy: {
        ...prev.normalization_strategy,
        tolerance: parseInt(value, 10) || 0
      }
    }));
  };

  // Add a handleApplyTemplate function
  const handleApplyTemplate = (template) => {
    
    // Check if this is our custom configuration
    if (template.original_config) {
      // Apply the saved custom configuration
      setFormValues(prev => ({
        ...prev,
        normalization_strategy: template.global_strategy
      }));
      return;
    }
    
    // Otherwise apply the template from database
    setFormValues(prev => ({
      ...prev,
      normalization_strategy: template.global_strategy
    }));
  };

  if (segmentLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (segmentError) {
    return (
      <Box sx={{ p: 2, textAlign: 'center' }}>
        <Typography variant="h6" color="error" gutterBottom>Error Loading Segment</Typography>
        <Typography variant="body2">{segmentError}</Typography>
        <Button 
          variant="outlined" 
          sx={{ mt: 2 }}
          onClick={() => navigate('/segments')}
        >
          Back to Segments
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', mb: 4 }}>
      {/* Header and breadcrumbs */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} aria-label="breadcrumb">
          <Link 
            component={RouterLink} 
            to="/segments"
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <SegmentsIcon sx={{ mr: 0.5 }} fontSize="small" />
            Segments
          </Link>
          <Typography color="text.primary">Edit Segment</Typography>
        </Breadcrumbs>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mt: 2 }}>
          <IconButton
            aria-label="back"
            onClick={handleCancel}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4">Edit Segment</Typography>
        </Box>
      </Box>
      
      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error.includes('plan allows a maximum of') ? 
            <>
              {error} 
              <Link href="/settings/billing" sx={{ ml: 1 }}>View Billing Options</Link>
            </> 
            : error
          }
        </Alert>
      )}
      
      <form onSubmit={handleSubmit}>
        <Paper sx={{ p: 3, mb: 4 }}>
          <Typography variant="h6" gutterBottom>Segment Configuration</Typography>
          
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <FieldLabel label="Segment Name" helpText={helpTexts.name} />
              <TextField
                name="name"
                value={formValues.name}
                onChange={handleInputChange}
                fullWidth
                error={!!formErrors.name}
                helperText={formErrors.name}
                required
              />
            </Grid>
            
            <Grid item xs={6}>
              <FieldLabel label="Time Interval" helpText="The time interval between data points in your segment" />
              <TextField
                name="interval"
                type="number"
                value={formValues.interval}
                onChange={handleInputChange}
                fullWidth
                error={!!formErrors.interval}
                helperText={formErrors.interval ? formErrors.interval : "Time between data points (in seconds)"}
                required
                InputProps={{
                  endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={6}>
              <FieldLabel label="Historical Window" helpText="How far back in time to include data" />
              <TextField
                name="tail_seconds"
                type="number"
                value={formValues.tail_seconds}
                onChange={handleInputChange}
                fullWidth
                InputProps={{
                  endAdornment: <InputAdornment position="end">seconds</InputAdornment>,
                }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <FieldLabel label="Real-time Processing" helpText="If enabled, uses current time as the end point; otherwise uses the last data point" />
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FormControlLabel
                  control={
                    <Switch
                      name="live"
                      checked={formValues.live}
                      onChange={handleInputChange}
                    />
                  }
                  label={formValues.live ? "Real-time (uses current time as endpoint)" : "Historical (uses last data point as endpoint)"}
                />
              </Box>
            </Grid>
          
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle1" gutterBottom>Fields Selection</Typography>
            </Grid>
            
            <Grid item xs={6}>
              <FieldLabel label="Features" helpText={helpTexts.features} />
              <Box 
                sx={{ 
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, 
                  p: 2, 
                  height: 200, 
                  overflowY: 'auto',
                  mb: 2
                }}
              >
                {availableFields.map(field => (
                  <FieldChoiceChip
                    key={field.key}
                    field={field}
                    disabled={formValues.features.includes(field.key)}
                    onClick={() => handleAddFeature(field.key)}
                  />
                ))}
                {availableFields.length === 0 && 
                  <Typography color="text.secondary">
                    No fields available. Please make sure you have defined signal fields in your workspace.
                  </Typography>
                }
              </Box>
              
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Selected Features:
              </Typography>
              <Box 
                sx={{ 
                  minHeight: 42, 
                  border: theme => formErrors.features ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, 
                  p: 1,
                  display: 'flex',
                  flexWrap: 'wrap'
                }}
              >
                {formValues.features.length > 0 ? (
                  formValues.features.map(feature => (
                    <SelectedItem 
                      key={feature} 
                      label={feature}
                      field={availableFields.find((f) => f.key === feature)}
                      onDelete={() => handleRemoveFeature(feature)} 
                    />
                  ))
                ) : (
                  <Typography color="text.secondary" sx={{ p: 1 }}>
                    No features selected
                  </Typography>
                )}
              </Box>
              {formErrors.features && (
                <FormHelperText error>{formErrors.features}</FormHelperText>
              )}
            </Grid>
            
            <Grid item xs={6}>
              <FieldLabel label="Labels" helpText={helpTexts.labels} />
              <Box 
                sx={{ 
                  border: theme => `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, 
                  p: 2, 
                  height: 200, 
                  overflowY: 'auto',
                  mb: 2
                }}
              >
                {availableFields.map(field => (
                  <FieldChoiceChip
                    key={field.key}
                    field={field}
                    disabled={formValues.labels.includes(field.key)}
                    onClick={() => handleAddLabel(field.key)}
                  />
                ))}
                {availableFields.length === 0 && 
                  <Typography color="text.secondary">
                    No fields available. Please make sure you have defined signal fields in your workspace.
                  </Typography>
                }
              </Box>
              
              <Typography variant="body2" fontWeight={500} gutterBottom>
                Selected Labels:
              </Typography>
              <Box 
                sx={{ 
                  minHeight: 42, 
                  border: theme => formErrors.labels ? `1px solid ${theme.palette.error.main}` : `1px solid ${theme.palette.divider}`,
                  borderRadius: 1, 
                  p: 1,
                  display: 'flex',
                  flexWrap: 'wrap'
                }}
              >
                {formValues.labels.length > 0 ? (
                  formValues.labels.map(label => (
                    <SelectedItem 
                      key={label} 
                      label={label}
                      field={availableFields.find((f) => f.key === label)}
                      onDelete={() => handleRemoveLabel(label)} 
                    />
                  ))
                ) : (
                  <Typography color="text.secondary" sx={{ p: 1 }}>
                    No labels selected
                  </Typography>
                )}
              </Box>
              {formErrors.labels && (
                <FormHelperText error>{formErrors.labels}</FormHelperText>
              )}
            </Grid>
            
            {fieldsData?.plan_limits && (
              <Grid item xs={12}>
                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    Your plan allows up to {fieldsData.plan_limits.features_in_segment} features and {fieldsData.plan_limits.labels_in_segment} labels per segment.
                  </Typography>
                </Alert>
              </Grid>
            )}
          
            <Grid item xs={12}>
              <Divider sx={{ my: 2 }} />
              <Typography variant="h6" gutterBottom>Data Normalization</Typography>
              <StrategyExplanation />
            </Grid>
            
            <Grid item xs={12}>
              <Paper elevation={0} variant="outlined" sx={{ p: 3, mb: 2 }}>
                <FeatureStrategyConfig
                  features={formValues.features}
                  normalizationStrategy={formValues.normalization_strategy}
                  segmentStrategies={formValues.segment_strategies || {}}
                  onStrategyChange={handlePrimaryStrategyChange}
                  onToleranceChange={handleStrategyToleranceChange}
                  onAddFallback={handleAddFallbackStrategy}
                  onRemoveFallback={handleRemoveFallbackStrategy}
                  onAddFieldStrategy={handleAddFieldStrategy}
                  onRemoveFieldStrategy={handleRemoveFieldStrategy}
                  formErrors={formErrors}
                  availableStrategies={availableStrategies || []}
                  templates={templatesData?.templates || []}
                  onApplyTemplate={handleApplyTemplate}
                />
              </Paper>
            </Grid>
          </Grid>
        </Paper>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
          <Button
            variant="outlined"
            onClick={handleCancel}
          >
            Cancel
          </Button>
          <CMButton
            type="submit"
            variant="contained"
            disabled={isLoading}
            startIcon={isLoading && <CircularProgress size={20} />}
          >
            {isLoading ? 'Saving...' : 'Save Changes'}
          </CMButton>
        </Box>
      </form>
    </Box>
  );
};

const mapStateToProps = (state) => ({
  segmentData: state.segment_detail.response?.segment,
  segmentLoading: state.segment_detail.isLoading,
  segmentError: state.segment_detail.error,
  isLoading: state.segment_edit.isLoading,
  response: state.segment_edit.response,
  error: state.segment_edit.error,
  fieldsData: state.segment_fields.response,
  fieldsLoading: state.segment_fields.isLoading,
  fieldsError: state.segment_fields.error,
  templatesData: state.segment_fields.templates,
  templatesLoading: state.segment_fields.templatesLoading
});

const mapDispatchToProps = (dispatch) => ({
  fetchSegmentDetail: (segmentId) => dispatch(fetchSegmentDetail(segmentId)),
  editSegment: (segmentId, data) => dispatch(editSegmentRequest(segmentId, data)),
  fetchAvailableFields: () => dispatch(fetchAvailableFields()),
  fetchTemplates: () => dispatch(fetchTemplatesRequest()),
  clearState: () => dispatch(clearState())
});

export default connect(mapStateToProps, mapDispatchToProps)(EditSegmentPage); 